// Quelle der Wahrheit für alle UI-Texte. `en.ts` muss strukturgleich sein —
// fehlende Übersetzungen sind damit Compile-Fehler, keine Laufzeit-Überraschungen.
export const de = {
  common: {
    cancel: "Abbrechen",
    save: "Speichern",
    create: "Anlegen",
    loading: "Lade …",
    deleteForever: "Endgültig löschen",
    optional: "optional",
    error: "Etwas ist schiefgelaufen.",
  },
  app: {
    updateBanner: "Neue Version verfügbar — hier klicken zum Neuladen",
    navTours: "Touren",
    switchAccount: "Account wechseln",
    newAccount: "Neuer Account …",
    newAccountTitle: "Neuen Account anlegen",
    newAccountPlaceholder: "Name der Firma",
    noAccountTitle: "Kein Account",
    noAccountText:
      "Du bist in keinem Account Mitglied. Bitte einen Admin eines Accounts " +
      "oder den Betreiber, dich hinzuzufügen.",
    noAccountOperatorText: "Du bist noch in keinem Account. Lege oben im Umschalter einen an.",
  },
  nav: {
    projects: "Projekte",
    noProjects: "Noch keine Projekte",
    noTours: "Keine Touren",
    tourCount: (n: number) => `${n} Tour${n === 1 ? "" : "en"}`,
    expand: "Aufklappen",
    collapse: "Zuklappen",
    menu: "Navigation",
    openMenu: "Menü öffnen",
    closeMenu: "Menü schließen",
    breadcrumb: "Pfad",
  },
  menu: {
    navigation: "Navigation",
    design: "Design",
    language: "Sprache",
    settings: "Einstellungen",
    signOut: "Abmelden",
  },
  settings: {
    title: "Einstellungen",
    navProfile: "Profil",
    navAccountGroup: "Account",
    navGeneral: "Allgemein",
    navUsers: "Benutzer",
  },
  theme: {
    light: "Hell",
    system: "System",
    dark: "Dunkel",
  },
  account: {
    title: "Profil",
    nameSection: "Name",
    nameChanged: "Name geändert.",
    emailSection: "E-Mail-Adresse",
    currentEmail: (email: string) => `Aktuell: ${email}`,
    newEmailPlaceholder: "neue@adresse.de",
    change: "Ändern",
    emailChanged: "E-Mail-Adresse geändert.",
    emailTaken: "Änderung nicht möglich — wird die Adresse bereits verwendet?",
    passwordSection: "Passwort",
    currentPassword: "Aktuelles Passwort",
    newPassword: (min: number) => `Neues Passwort (min. ${min} Zeichen)`,
    changePassword: "Passwort ändern",
    passwordChanged: "Passwort geändert — andere Geräte wurden abgemeldet.",
    wrongPassword: "Aktuelles Passwort ist falsch.",
  },
  company: {
    title: "Account",
    nameLabel: "Name des Accounts",
    nameChanged: "Name geändert.",
    memberHint: "Änderungen am Account nimmt ein Admin vor.",
    storageSection: "Speicherverbrauch",
    storageHint: "Originalbilder plus alle gerenderten Varianten und Poster.",
    tourCount: (n: number) => `${n} Tour${n === 1 ? "" : "en"}`,
    billingHint: "Abrechnung & Tarife folgen später an dieser Stelle.",
    loadError: "Account-Daten konnten nicht geladen werden.",
  },
  twofa: {
    section: "Zwei-Faktor-Authentifizierung",
    statusOn: "Aktiv",
    statusOff: "Inaktiv",
    enabledHint:
      "Beim Anmelden wird ein Code an deine E-Mail geschickt. Zum Deaktivieren Passwort eingeben.",
    disabledHint:
      "Beim Anmelden zusätzlich einen Code per E-Mail abfragen. Zum Aktivieren Passwort eingeben.",
    enable: "Aktivieren",
    disable: "Deaktivieren",
    enabledMsg: "Zwei-Faktor-Authentifizierung ist jetzt aktiv.",
    disabledMsg: "Zwei-Faktor-Authentifizierung ist deaktiviert.",
    title: "Bestätigungscode",
    prompt: "Wir haben dir einen Anmeldecode per E-Mail geschickt.",
    sent: "Code gesendet — bitte Posteingang prüfen.",
    sendFailed: "Code konnte nicht gesendet werden.",
    wrongCode: "Code ungültig oder abgelaufen.",
    trustDevice: "Diesem Gerät 30 Tage vertrauen",
    verify: "Anmelden",
    resend: "Code erneut senden",
    back: "Zurück zur Anmeldung",
  },
  login: {
    tagline: "360°-Touren für Immobilien",
    email: "E-Mail",
    password: "Passwort",
    submit: "Anmelden",
    errorBanned: "Dieses Konto ist gesperrt.",
    errorTooMany: "Zu viele Versuche — bitte kurz warten.",
    errorOrigin: "Falsche URL — bitte über die richtige Adresse anmelden.",
    errorInvalid: "E-Mail oder Passwort falsch.",
    forgot: "Passwort vergessen?",
    forgotTitle: "Passwort zurücksetzen",
    forgotText: "Wir schicken dir einen Link zum Zurücksetzen an deine E-Mail-Adresse.",
    forgotSubmit: "Link anfordern",
    forgotSent:
      "Wenn die Adresse bei uns existiert, ist ein Link unterwegs — bitte Posteingang prüfen.",
  },
  reset: {
    title: "Neues Passwort setzen",
    passwordPlaceholder: (min: number) => `Neues Passwort (min. ${min} Zeichen)`,
    submit: "Passwort speichern",
    success: "Passwort geändert — du kannst dich jetzt anmelden.",
    invalid: "Der Link ist ungültig oder abgelaufen.",
    requestNew: "Neuen Link anfordern",
    toLogin: "Zur Anmeldung",
    failed: "Passwort konnte nicht gesetzt werden — bitte neuen Link anfordern.",
  },
  invite: {
    title: "Einladung",
    intro: (account: string, role: string) => `Du wurdest als ${role} in „${account}“ eingeladen.`,
    newUserText: (email: string) =>
      `Zugang für ${email} anlegen: Name und Passwort wählen, danach geht es direkt los.`,
    namePlaceholder: "Dein Name",
    createAndAccept: "Zugang anlegen und beitreten",
    signInFirst:
      "Für diese E-Mail gibt es schon einen Zugang. Melde dich an, dann kannst du die Einladung annehmen.",
    acceptAs: (email: string) => `Angemeldet als ${email}.`,
    accept: "Einladung annehmen",
    mismatch: (current: string, invited: string) =>
      `Du bist als ${current} angemeldet, die Einladung gilt aber für ${invited}. Melde dich ab und öffne den Link erneut.`,
    invalid: "Dieser Einladungslink ist ungültig oder wurde zurückgezogen.",
    expired: "Dieser Einladungslink ist abgelaufen — bitte um eine neue Einladung.",
    used: "Diese Einladung wurde bereits angenommen.",
  },
  dashboard: {
    title: "Touren",
    newTour: "Neue Tour",
    emptyTitle: "Noch keine Touren",
    emptyText: "Lege die erste Tour an und lade 360°-Bilder hoch.",
    firstTour: "Erste Tour anlegen",
    noScenes: "Noch keine Szenen",
    sceneCount: (n: number) => `${n} Szene${n === 1 ? "" : "n"}`,
    readySuffix: (n: number) => ` · ${n} bereit`,
    statusDraft: "Entwurf",
    statusPublished: "Veröffentlicht",
    statusPublishedChanges: "Veröffentlicht · Änderungen offen",
    statusLocked: "Passwortgeschützt",
    createTitle: "Neue Tour",
    tourNameLabel: "Name der Tour",
    tourNamePlaceholder: "z. B. Musterstraße 1, EG",
    projectLabel: "Projekt (Objekt)",
    newProjectOption: "Neues Projekt (Name der Tour)",
    editProjectTooltip: "Projekt bearbeiten",
  },
  project: {
    title: "Projekt",
    emptyTitle: "Noch keine Touren in diesem Objekt",
    editTitle: "Projekt bearbeiten",
    nameLabel: "Name (Objekt)",
    addressLabel: "Adresse",
    addressPlaceholder: "Musterstraße 1, 12345 Berlin",
    moreActions: "Weitere Aktionen",
    edit: "Projekt bearbeiten",
    delete: "Projekt löschen",
    deleteTitle: (name: string) => `Projekt „${name}“ löschen?`,
    deleteText: (n: number) =>
      n === 1
        ? "Die Tour dieses Objekts wird mit allen Szenen, Bildern und Hotspots endgültig gelöscht."
        : `Alle ${n} Touren dieses Objekts werden mit allen Szenen, Bildern und Hotspots endgültig gelöscht.`,
    deleteTextEmpty: "Das Objekt enthält keine Touren und wird entfernt.",
    deletePublished:
      "Veröffentlichte Touren sind dabei — ihre Links funktionieren danach nicht mehr.",
  },
  tour: {
    settings: "Einstellungen",
    tabScenes: "Szenen",
    settingsSaved: "Gespeichert",
    moreActions: "Weitere Aktionen",
    delete: "Löschen",
    preview: "Vorschau",
    previewHint: "Aktuellen Entwurf ansehen — nur für angemeldete Benutzer",
    settingsTitle: "Tour-Einstellungen",
    sectionGeneral: "Allgemein",
    sectionDisplay: "Darstellung",
    nameLabel: "Name",
    viewLimit: "Stativ ausblenden",
    viewLimitHint: "Begrenzt in der Tour den Blick nach unten — das Stativ bleibt unsichtbar.",
    viewLimitOff: "Aus",
    viewLimitSoft: "Dezent (bis −75°)",
    viewLimitMedium: "Mittel (bis −60°)",
    viewLimitStrong: "Stark (bis −45°)",
    gyroButton: "Bewegungssteuerung",
    gyroButtonHint:
      "Zeigt auf Handy und Tablet einen Knopf, mit dem man sich durch Drehen des Geräts umsieht.",
    sectionHotspots: "Hotspots",
    hotspotHint: "Aussehen der Navigationspunkte in dieser Tour.",
    hotspotPreview: "Beispielansicht",
    hotspotPreviewSample: "Beispiel",
    hotspotVariant: "Stil",
    hotspotVariantDot: "Punkt",
    hotspotVariantRing: "Ring",
    hotspotVariantArrow: "Pfeil",
    hotspotVariantPill: "Beschriftung",
    hotspotColor: "Farbe",
    hotspotSize: "Größe",
    hotspotSizeS: "Klein",
    hotspotSizeM: "Mittel",
    hotspotSizeL: "Groß",
    hotspotPulse: "Pulsieren",
    deleteTitle: (name: string) => `Tour „${name}“ löschen?`,
    deleteText: "Alle Szenen, Bilder und Hotspots dieser Tour werden endgültig gelöscht.",
    dropTitle: "360°-Bilder hierher ziehen",
    dropText:
      "oder klicken zum Auswählen — equirektangulare JPGs (2:1), z. B. Insta360-Studio-Export",
    dropOverlayTitle: "360°-Bilder ablegen",
    orDrop: "oder Bilder einfach auf die Seite ziehen",
    addScenes: "Szenen hinzufügen",
    openScene: "Im Rundgang öffnen",
    dragToSort: "Ziehen zum Sortieren",
    hotspotCount: (n: number) => `${n} Hotspot${n === 1 ? "" : "s"}`,
    hotspotNone: "Keine Hotspots",
    renameScene: "Umbenennen",
    replaceScene: "Bild ersetzen",
    deleteScene: "Löschen",
    renameSceneTitle: "Szene umbenennen",
    deleteSceneTitle: (name: string) => `Szene „${name}“ löschen?`,
    deleteSceneText: "Bild und alle Varianten werden endgültig gelöscht.",
    deleteScenePublished:
      "In der veröffentlichten Tour bleibt die Szene sichtbar, bis du die Änderungen veröffentlichst.",
    replaceSceneTitle: (name: string) => `Bild von „${name}“ ersetzen`,
    replaceSceneText:
      "Hotspots, Verlinkungen, Name und Startansicht bleiben erhalten — nur das Bild wird getauscht. Sinnvoll, wenn du dieselbe Aufnahme nachbearbeitet hast (Belichtung, Retusche).",
    replaceScenePublished:
      "Draußen bleibt das alte Bild sichtbar, bis du die Änderungen veröffentlichst.",
    replaceSceneWarn:
      "Ist das Bild anders ausgerichtet als das alte, sitzen die Hotspots nicht mehr an ihrem Platz.",
    replaceScenePick: "Bild auswählen",
    restoreImage: "Vorheriges Bild wiederherstellen",
    failedBanner: (n: number) =>
      n === 1
        ? "Ein Bild konnte nicht übernommen werden — Details stehen auf der Karte."
        : `${n} Bilder konnten nicht übernommen werden — Details stehen auf den Karten.`,
    removeFailed: (n: number) => (n === 1 ? "Entfernen" : `Alle ${n} entfernen`),
  },
  // Upload-/Verarbeitungsanzeige der Tour-Seite (Sammelleiste + Kartenzustände)
  upload: {
    titleUploading: "Bilder werden hochgeladen",
    titleProcessing: "Bilder werden verarbeitet",
    uploading: (done: number, total: number) => `${done} von ${total} hochgeladen`,
    processing: (done: number, total: number) => `${done} von ${total} verarbeitet`,
    remaining: (text: string) => `noch ca. ${text}`,
    minutes: (n: number) => `${n} min`,
    seconds: (n: number) => `${n} s`,
    failed: (n: number) => `${n} fehlgeschlagen`,
    allDone: (n: number) => `${n} ${n === 1 ? "Szene ist" : "Szenen sind"} fertig`,
    dismiss: "Ausblenden",
    leaveTitle: "Uploads laufen noch",
    leaveText: (running: number, done: number) =>
      `${running === 1 ? "Ein Upload läuft" : `${running} Uploads laufen`} noch. Wenn du die Seite ` +
      "verlässt, werden sie abgebrochen und die betroffenen Szenen wieder entfernt" +
      (done > 0
        ? ` — ${done === 1 ? "das bereits hochgeladene Bild bleibt" : `die ${done} bereits hochgeladenen Bilder bleiben`}.`
        : "."),
    leaveStay: "Hier bleiben",
    leaveAnyway: "Trotzdem verlassen",
    stateWaiting: "Wartet",
    stateUploading: (pct: number) => `Lädt hoch ${pct} %`,
    stateQueued: (pos: number) => `Warteschlange · Platz ${pos}`,
    stateProcessing: "Wird verarbeitet",
    stateReady: "Fertig",
    phaseAnalyze: "Bild wird geprüft",
    phaseTiles: "Kacheln werden erzeugt",
    phaseBases: "Basisbilder werden erzeugt",
    phasePoster: "Vorschaubild wird erzeugt",
  },
  pipeline: {
    unreadable: "Kein lesbares JPG/PNG/WebP-Bild.",
    wrongRatio: (ratio: string) =>
      `Kein 360°-Bild: Seitenverhältnis ist ${ratio}:1 statt 2:1 (equirektangular). ` +
      "Dual-Fisheye bitte in Insta360 Studio als 360°-JPG exportieren.",
    tooSmall: (px: string) => `Auflösung zu niedrig: ${px} px Breite (Minimum 4096 px).`,
    noOriginal: "Kein Original hochgeladen.",
    failed: "Verarbeitung fehlgeschlagen — bitte erneut versuchen.",
  },
  password: {
    tooShort: (min: number) => `Mindestens ${min} Zeichen.`,
    tooLong: (max: number) => `Höchstens ${max} Zeichen.`,
    personal: "Das Passwort darf keine Teile deiner E-Mail-Adresse oder deines Namens enthalten.",
    weak: "Zu leicht zu erraten — nimm einen längeren Satz oder mehrere zufällige Wörter.",
    compromised:
      "Dieses Passwort ist in bekannten Datenlecks aufgetaucht — bitte ein anderes wählen.",
    strength: (score: number) => ["Sehr schwach", "Schwach", "Mittel", "Gut", "Stark"][score] ?? "",
  },
  apiError: {
    unauthorized: "Nicht angemeldet — bitte neu anmelden.",
    forbidden: "Keine Berechtigung.",
    invalidInput: "Ungültige Eingabe.",
    tourNotFound: "Tour nicht gefunden.",
    sceneNotFound: "Szene nicht gefunden.",
    hotspotNotFound: "Hotspot nicht gefunden.",
    projectNotFound: "Projekt nicht gefunden.",
    accountNotFound: "Account nicht gefunden.",
    noAccount: "Du bist in keinem Account Mitglied.",
    alreadyMember: "Diese Person ist bereits Mitglied.",
    invalidRole: "Ungültige Rolle.",
    invitationInvalid: "Der Einladungslink ist ungültig.",
    invitationExpired: "Der Einladungslink ist abgelaufen.",
    emailMismatch: "Die Einladung gilt für eine andere E-Mail-Adresse.",
    userExists: "Für diese E-Mail gibt es schon einen Zugang — bitte anmelden.",
    invalidTargetScene: "Ziel-Szene ungültig.",
    unsupportedMediaType: "Nur JPG/PNG/WebP — bitte in Insta360 Studio als 360°-JPG exportieren.",
    sceneProcessing: "Szene wird gerade verarbeitet.",
    fileTooLarge: "Datei zu groß (max. 200 MB).",
    emptyUpload: "Leerer Upload.",
    uploadFailed: "Upload fehlgeschlagen — bitte erneut versuchen.",
    noReadyScene: "Keine fertige Szene — es gibt nichts zu veröffentlichen.",
    noPreviousImage: "Das vorherige Bild ist nicht mehr verfügbar.",
    publishedSceneNotReady:
      "Eine bereits veröffentlichte Szene ist gerade nicht fertig — bitte die Verarbeitung abwarten oder das vorherige Bild wiederherstellen.",
    sceneListMismatch: "Szenenliste stimmt nicht mit der Tour überein.",
    network: "Netzwerkfehler — bitte Verbindung prüfen.",
    http: (status: number) => `Fehler (${status})`,
  },
  share: {
    publish: "Veröffentlichen",
    republish: "Änderungen veröffentlichen",
    unpublish: "Zurückziehen",
    share: "Teilen",
    title: "Veröffentlichen & teilen",
    draftState: "Noch nicht veröffentlicht — der Link funktioniert erst nach dem Veröffentlichen.",
    draftNoScene: "Sobald die erste Szene fertig ist, kannst du veröffentlichen.",
    publishedState: (date: string) => `Veröffentlichter Stand: ${date}`,
    upToDate: "Aktuell",
    pendingChanges: "Änderungen offen",
    republishHint: "Änderungen im Editor werden erst mit „Änderungen veröffentlichen“ öffentlich.",
    blockedHint:
      "Eine veröffentlichte Szene ist gerade nicht fertig — bis dahin würde sie beim Veröffentlichen aus der Tour fallen. Warte die Verarbeitung ab oder stelle auf der Karte das vorherige Bild wieder her.",
    linksAfterPublish:
      "Öffentlicher Link und Einbettungscode erscheinen hier nach dem Veröffentlichen.",
    showPassword: "Passwort anzeigen",
    hidePassword: "Passwort verbergen",
    publicLink: "Öffentlicher Link",
    embedCode: "iframe-Code zum Einbetten",
    copy: "Kopieren",
    copied: "Kopiert ✓",
    openTour: "Tour öffnen",
    password: "Passwortschutz",
    passwordHint:
      "Optional — Besucher müssen das Passwort eingeben. Wirkt sofort, ohne Veröffentlichen.",
    passwordSet: "Passwort ist gesetzt — zum Ändern überschreiben.",
    passwordPlaceholder: "Passwort (min. 4 Zeichen)",
    passwordSave: "Setzen",
    passwordRemove: "Entfernen",
    unpublishTitle: "Veröffentlichung zurückziehen?",
    unpublishText:
      "Der öffentliche Link und alle Embeds zeigen danach „nicht gefunden“, bis du erneut veröffentlichst.",
  },
  stats: {
    title: "Statistik",
    viewsPerDay: "Aufrufe und Besucher pro Tag",
    range30: "30 Tage",
    range90: "90 Tage",
    rangeAll: "Gesamte Zeit",
    sources: "Quellen",
    devices: "Geräte",
    direct: "Direkt / ohne Quelle",
    mobile: "Mobil",
    desktop: "Desktop",
    empty: "Noch keine Aufrufe im Zeitraum.",
    loadError: "Statistik konnte nicht geladen werden.",
    views: "Aufrufe",
    visitors: "Besucher",
    overview: (days: number) => `Letzte ${days} Tage · alle Touren`,
    visitorsHintAll: "eindeutig je Tag und Tour",
    visitorsHint: "eindeutig je Tag, ohne Cookies",
    avgDuration: "Ø Verweildauer",
    avgDurationHint: "nur sichtbare Zeit",
    bounceRate: "Absprungrate",
    bounceHint: "unter 10 s, keine Interaktion",
    interactionRate: "Interaktionsquote",
    interactionHint: "gedreht, Szene gewechselt, Hotspot",
    hotspotClicks: "Hotspot-Klicks",
    fullscreen: "Vollbild genutzt",
    trend: "Verlauf",
    duration: "Verweildauer",
    durationHint:
      "Zählt nur, was wirklich angeschaut wird: Tab aktiv, Tour im Bild, Besucher nicht länger als eine Minute untätig.",
    buckets: ["unter 10 s", "10–30 s", "30–60 s", "1–3 min", "über 3 min"],
    sessionsCaption: (n: number) => `${n} abgeschlossene Sitzung${n === 1 ? "" : "en"}`,
    pendingHint: "Laufende Sitzungen kommen nach ihrem Ende dazu, spätestens nach 10 Minuten.",
    scenes: "Szenen",
    sceneCol: "Szene",
    sceneViews: "Aufrufe",
    sceneReach: "Anteil der Sitzungen",
    sceneAvg: "Ø Zeit",
    sceneEntries: "Einstieg",
    sceneExits: "Ausstieg",
    scenesEmpty: "Noch keine Szenen-Daten im Zeitraum.",
    languages: "Sprachen",
    engagement: "Nutzung",
    seconds: (s: number) => `${s} s`,
    minutes: (m: number, s: number) => `${m}:${String(s).padStart(2, "0")} min`,
  },
  editor: {
    editTour: "Rundgang bearbeiten",
    smallScreenTitle: "Zu wenig Platz",
    smallScreenText:
      "Der Rundgang-Editor braucht einen größeren Bildschirm — Hotspots setzt du per Ziehen im Panorama. Öffne ihn am Tablet (quer) oder am Rechner.",
    back: "Zur Tour",
    close: "Schließen",
    settings: "Einstellungen",
    mobilePreview: "Mobile Vorschau",
    mobilePreviewOff: "Zurück zur großen Ansicht",
    scenes: "Szenen",
    hotspots: "Hotspots in dieser Szene",
    noHotspots: "Noch keine Hotspots — klicke ins Panorama, um einen zu setzen.",
    clickHint: "Klick ins Bild setzt einen Hotspot",
    noReadyScenes: "Noch keine fertigen Szenen — lade zuerst 360°-Bilder hoch.",
    placeTitle: "Navigations-Hotspot",
    hotspotDrawerTitle: "Hotspot bearbeiten",
    placeTarget: "Ziel-Szene",
    placeLabel: "Beschriftung",
    placeLabelHint: "leer = Name der Ziel-Szene",
    target: "Ziel",
    arrival: "Ankunftsansicht",
    arrivalAuto: "Automatisch (Startansicht der Zielszene)",
    arrivalManual: "Manuell festgelegt",
    arrivalStart: "Ansicht festlegen …",
    arrivalBanner: "Ansicht in der Zielszene einstellen, dann speichern.",
    arrivalSave: "Als Ankunft speichern",
    arrivalReset: "Zurück auf Automatik",
    reposition: "Position neu setzen",
    repositionBanner: "Klicke im Bild auf die neue Position.",
    deleteHotspot: "Hotspot löschen",
    startView: "Als Startansicht speichern",
    startViewSaved: "Startansicht gespeichert",
    startViewDirty: "Diese Ansicht ist nicht als Start gespeichert — klicken zum Speichern",
    startViewClean: "Entspricht der gespeicherten Startansicht",
    startViewReset: "Zur gespeicherten Startansicht zurückspringen",
  },
  members: {
    title: "Benutzer",
    add: "Benutzer hinzufügen",
    addTitle: "Person hinzufügen",
    inviteHint:
      "Bestehende Zugänge werden sofort Mitglied. Neue Personen bekommen eine Einladung " +
      "per E-Mail (7 Tage gültig) und setzen ihr Passwort selbst.",
    invited: "Einladung verschickt.",
    added: "Person hinzugefügt.",
    pendingTitle: "Offene Einladungen",
    pendingHint:
      "Noch nicht angenommen. Erneut senden erzeugt einen neuen Link mit frischer Frist; " +
      "der Link lässt sich auch kopieren und selbst verschicken.",
    copyLink: "Einladungslink kopieren",
    linkCopied: "Link kopiert.",
    validUntil: "Gültig bis",
    resend: "Erneut senden",
    resent: "Einladung erneut verschickt.",
    cancelInvite: "Zurückziehen",
    canceled: "Einladung zurückgezogen.",
    name: "Name",
    email: "E-Mail",
    role: "Rolle",
    roleOwner: "Admin",
    roleMember: "Mitglied",
    you: "(du)",
    tooltipRemove: "Aus dem Account entfernen",
    lastOwnerHint: "Letzter Admin — mindestens einer muss bleiben.",
    removeTitle: (name: string) => `„${name}“ aus dem Account entfernen?`,
    removeText:
      "Die Person verliert den Zugriff auf diesen Account. Ihr Zugang und " +
      "andere Accounts bleiben erhalten.",
    removeSelfText:
      "Du verlierst den Zugriff auf diesen Account und landest danach in deinem " +
      "nächsten Account.",
    remove: "Entfernen",
    listError: "Mitgliederliste konnte nicht geladen werden.",
    actionError: "Aktion fehlgeschlagen.",
  },
};

export type Dictionary = typeof de;
